import type { BannerSwap } from "../../utils/@types";
import {
  formatSwapDateCell,
  toBannerSwapDate,
} from "./bannerDashboardUtils";
import { bannerSwapIntervalsOverlap } from "../BannerSwap/BannerSwapComponents/bannerSwapOverlapUtils";
import { isValid } from "date-fns";

export type SwapScheduleInfoKind =
  | "overlap"
  | "delayed"
  | "blocked"
  | "adjusted";

export type SwapScheduleInfoLine = {
  channelId: string;
  effectiveStart: string;
  effectiveEnd: string;
  note: string;
  kind: SwapScheduleInfoKind;
  headline: string;
  subtext?: string;
};

export type SwapScheduleColumnInfo = {
  lines: SwapScheduleInfoLine[];
  ariaSummary: string;
};

function getChannelEffectiveDates(
  swap: BannerSwap,
  channelId: string
): { startDate: Date; endDate: Date } {
  const override = swap.channelSchedules?.[channelId];
  if (override) {
    return {
      startDate: toBannerSwapDate(override.startDate),
      endDate: toBannerSwapDate(override.endDate),
    };
  }
  return {
    startDate: toBannerSwapDate(swap.startDate),
    endDate: toBannerSwapDate(swap.endDate),
  };
}

function datesDiffer(a: Date, b: Date): boolean {
  return (
    isValid(a) &&
    isValid(b) &&
    Math.abs(a.getTime() - b.getTime()) > 1000
  );
}

function scheduleStartLine(
  swap: BannerSwap,
  channelId: string,
  startDate: Date,
  endDate: Date
): SwapScheduleInfoLine {
  const schedule = swap.channelSchedules?.[channelId];
  const swapStart = toBannerSwapDate(swap.startDate);
  const effectiveStart = formatSwapDateCell(startDate);
  const effectiveEnd = formatSwapDateCell(endDate);

  if (schedule?.blockedBySwapUID) {
    const headline = `Starts after swap #${schedule.blockedBySwapUID}`;
    return {
      channelId,
      effectiveStart,
      effectiveEnd,
      kind: "blocked",
      headline,
      subtext: "Waiting for the blocking swap to finish",
      note: `${headline} completes`,
    };
  }
  if (schedule?.delayedBySwapUID) {
    const original = schedule.originalStartDate
      ? formatSwapDateCell(schedule.originalStartDate)
      : formatSwapDateCell(swap.startDate);
    const headline = `Delayed by swap #${schedule.delayedBySwapUID}`;
    return {
      channelId,
      effectiveStart,
      effectiveEnd,
      kind: "delayed",
      headline,
      subtext: `Originally ${original}`,
      note: `${headline}; originally ${original}`,
    };
  }
  const headline = "Start time adjusted";
  return {
    channelId,
    effectiveStart,
    effectiveEnd,
    kind: "adjusted",
    headline,
    subtext: `Was ${formatSwapDateCell(swapStart)}`,
    note: `Adjusted from ${formatSwapDateCell(swapStart)}`,
  };
}

function scheduleEndLine(
  swap: BannerSwap,
  channelId: string,
  startDate: Date,
  endDate: Date
): SwapScheduleInfoLine {
  const schedule = swap.channelSchedules?.[channelId];
  const swapEnd = toBannerSwapDate(swap.endDate);
  const effectiveStart = formatSwapDateCell(startDate);
  const effectiveEnd = formatSwapDateCell(endDate);

  if (schedule?.blockedBySwapUID) {
    const headline = `Tied to swap #${schedule.blockedBySwapUID}`;
    return {
      channelId,
      effectiveStart,
      effectiveEnd,
      kind: "blocked",
      headline,
      subtext: "End follows overlap resolution",
      note: `Ends before swap #${schedule.blockedBySwapUID} window`,
    };
  }
  const headline = "End time adjusted";
  return {
    channelId,
    effectiveStart,
    effectiveEnd,
    kind: "adjusted",
    headline,
    subtext: `Was ${formatSwapDateCell(swapEnd)}`,
    note: `Adjusted from ${formatSwapDateCell(swapEnd)}`,
  };
}

function buildAriaSummary(
  field: "start" | "end",
  lines: SwapScheduleInfoLine[]
): string {
  const intro =
    field === "start"
      ? "Channel start schedule details."
      : "Channel end schedule details.";
  const parts = lines.map(
    (line) =>
      `${line.channelId}: ${line.effectiveStart} to ${line.effectiveEnd}. ${line.note}`
  );
  return `${intro} ${parts.join(" ")}`;
}

function peerOverlapsForChannel(
  swap: BannerSwap,
  channelId: string,
  queue: BannerSwap[]
): SwapScheduleInfoLine[] {
  const { startDate, endDate } = getChannelEffectiveDates(swap, channelId);
  if (!isValid(startDate) || !isValid(endDate)) {
    return [];
  }

  const lines: SwapScheduleInfoLine[] = [];
  for (const peer of queue) {
    if (peer.swapUID === swap.swapUID) {
      continue;
    }
    if (!["scheduled", "running"].includes(peer.status)) {
      continue;
    }
    if (!peer.channelIDs.includes(channelId)) {
      continue;
    }
    const peerStart = toBannerSwapDate(peer.startDate);
    const peerEnd = toBannerSwapDate(peer.endDate);
    if (
      !isValid(peerStart) ||
      !isValid(peerEnd) ||
      !bannerSwapIntervalsOverlap(startDate, endDate, peerStart, peerEnd)
    ) {
      continue;
    }
    lines.push({
      channelId,
      effectiveStart: formatSwapDateCell(startDate),
      effectiveEnd: formatSwapDateCell(endDate),
      kind: "overlap",
      headline: `Overlaps swap #${peer.swapUID}`,
      subtext: `${peer.swapName} · ${formatSwapDateCell(peer.startDate)} – ${formatSwapDateCell(peer.endDate)}`,
      note: `Overlaps swap #${peer.swapUID} ${peer.swapName} (${formatSwapDateCell(peer.startDate)} – ${formatSwapDateCell(peer.endDate)})`,
    });
  }
  return lines;
}

export function getSwapStartColumnInfo(
  swap: BannerSwap,
  queue: BannerSwap[]
): SwapScheduleColumnInfo | null {
  const lines: SwapScheduleInfoLine[] = [];
  const seenChannels = new Set<string>();
  const swapStart = toBannerSwapDate(swap.startDate);

  for (const channelId of swap.channelIDs) {
    const { startDate, endDate } = getChannelEffectiveDates(swap, channelId);
    if (!isValid(startDate) || !isValid(endDate)) {
      continue;
    }
    if (datesDiffer(startDate, swapStart)) {
      lines.push(scheduleStartLine(swap, channelId, startDate, endDate));
      seenChannels.add(channelId);
    }
  }

  for (const channelId of swap.channelIDs) {
    if (seenChannels.has(channelId)) {
      continue;
    }
    for (const overlapLine of peerOverlapsForChannel(swap, channelId, queue)) {
      lines.push(overlapLine);
      seenChannels.add(channelId);
      break;
    }
  }

  if (lines.length === 0) {
    return null;
  }
  return {
    lines,
    ariaSummary: buildAriaSummary("start", lines),
  };
}

export function getSwapEndColumnInfo(
  swap: BannerSwap,
  queue: BannerSwap[]
): SwapScheduleColumnInfo | null {
  const lines: SwapScheduleInfoLine[] = [];
  const swapEnd = toBannerSwapDate(swap.endDate);

  for (const channelId of swap.channelIDs) {
    const { startDate, endDate } = getChannelEffectiveDates(swap, channelId);
    if (!isValid(startDate) || !isValid(endDate)) {
      continue;
    }
    if (datesDiffer(endDate, swapEnd)) {
      lines.push(scheduleEndLine(swap, channelId, startDate, endDate));
    }
  }

  if (lines.length === 0) {
    return null;
  }
  return {
    lines,
    ariaSummary: buildAriaSummary("end", lines),
  };
}
